import { isBriefFolder } from "@/domains/documents/utils/briefFolder";
import React, { useMemo, useCallback, useEffect, useRef } from "react";
import { Plus } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TreeFolder } from "../../../types";
import { RootFolderItem } from "../../molecules/FolderTree/RootFolderItem";
import { FolderTreeNode } from "./FolderTreeNode";
import { useFolderExpansion } from "../../../hooks/useFolderExpansion";
import { useFolderDragDrop } from "../../../hooks/useFolderDragDrop";
import { useFolderSelection } from "../../../hooks/useFolderSelection";
import { useFolderRename } from "../../../hooks/useFolderRename";
import { useMoveDragOrigin } from "../../../hooks/useMoveDragOrigin";
import { useMoveDragTeardown } from "../../../hooks/useMoveDragTeardown";
import {
  isEligibleMoveDropTarget,
  writeMoveDragPayload,
  type MoveDragSelection,
} from "../../../utils/documentMoveDrag";
import { isSystemFolder } from "../../../services/folderService";
import type { UploadSelection } from "@/domains/documents/utils/uploadSelection";

interface FolderTreeProps {
  /** Array of root-level folders */
  folders: TreeFolder[];
  /** Search query for filtering folders */
  searchQuery?: string;
  /** Total document count across all folders */
  totalDocumentCount?: number;
  /** Controlled selected folder ID (synced from parent) */
  selectedFolderId?: string | null;
  /** Called when a document is dropped on a folder */
  onDropDocument?: (documentId: string, targetFolderId: string | null) => void;
  /** Called when a folder is moved to another folder */
  onMoveFolder?: (folderId: string, targetFolderId: string | null) => void;
  /** Called when multiple items are dropped */
  onDropSelection?: (docIds: string[], folderIds: string[], targetFolderId: string | null) => void;
  /** Called when OS files are dropped on a folder row (routes the upload to that folder) */
  onDropFiles?: (selection: UploadSelection, targetFolderId: string | null) => void;
  /** Active table drag; used to expose every valid hierarchy destination. */
  activeMove?: MoveDragSelection | null;
  /** Current table selection, carried when a selected tree folder starts the drag. */
  dragSelection?: MoveDragSelection;
  onInternalDragChange?: (selection: MoveDragSelection | null) => void;
  /** Called when a folder is renamed */
  onRenameFolder?: (folderId: string, newName: string) => void;
  /** Called when a folder is deleted */
  onDeleteFolder?: (folderId: string) => void;
  /** Called when a subfolder is created */
  onCreateSubfolder?: (parentFolderId: string | null) => void;
  /** Called when a folder is exported */
  onExportFolder?: (folderId: string | null) => void;
  /** Opens the AI organize dialog for a folder (WHA-3654). */
  onOrganizeFolder?: (folderId: string) => void;
  /** Called when a folder is shared */
  onShareFolder?: (folderId: string) => void;
  /** Called when a folder is selected */
  onFolderSelect?: (folderId: string) => void;
  /** Called when root folder is selected */
  onSelectRoot?: () => void;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state message */
  emptyMessage?: string;
  /** Hide the "Files" root item (for project-scoped views where project name is shown separately) */
  hideRootItem?: boolean;
  /**
   * Label of the root item. Hosts that navigate the project root from the tree
   * (/documents) pass the project name, so the root reads as the project the
   * page nav names instead of a generic "Files".
   */
  rootName?: string;
}

/**
 * FolderTree - Organism component
 *
 * Complete folder tree with root + search + empty state.
 * Uses FolderTreeNode for recursive rendering, RootFolderItem for root handling.
 * Integrates useFolderExpansion, useFolderDragDrop, useFolderSelection, and useFolderRename hooks.
 * Handles search filtering, empty states, and loading states.
 * Part of the atomic design system for folder tree display.
 *
 * @example
 * ```tsx
 * <FolderTree
 *   folders={folders}
 *   searchQuery={searchQuery}
 *   totalDocumentCount={42}
 *   onDropDocument={handleDropDocument}
 *   onMoveFolder={handleMoveFolder}
 *   onRenameFolder={handleRenameFolder}
 *   onDeleteFolder={handleDeleteFolder}
 * />
 * ```
 */
export const FolderTree = React.memo(function FolderTree({
  folders,
  searchQuery = "",
  totalDocumentCount = 0,
  selectedFolderId: controlledSelectedFolderId,
  onDropDocument,
  onMoveFolder,
  onDropSelection,
  onDropFiles,
  activeMove = null,
  dragSelection = { docIds: [], folderIds: [] },
  onInternalDragChange,
  onRenameFolder,
  onDeleteFolder,
  onCreateSubfolder,
  onExportFolder,
  onOrganizeFolder,
  onShareFolder,
  onFolderSelect,
  onSelectRoot,
  isLoading = false,
  emptyMessage = "No folders yet. Create your first folder to get started.",
  hideRootItem = false,
  rootName,
}: FolderTreeProps) {
  const { t } = useLanguage();
  // Initialize hooks
  const { isExpanded, toggleExpanded, expandFolder, startHoverExpand, cancelHoverExpand } =
    useFolderExpansion();
  const { selectedFolderId: internalSelectedFolderId, selectFolder } = useFolderSelection();

  // Use controlled selectedFolderId if provided, otherwise use internal state
  const selectedFolderId =
    controlledSelectedFolderId !== undefined
      ? controlledSelectedFolderId
      : internalSelectedFolderId;

  // Helper to find all ancestor folder IDs for a given folder.
  // Returns the ancestor IDs (outermost first), or `null` when the folder is
  // not in this branch. The null sentinel is essential: an empty array means
  // "found here, no ancestors", so returning [] for "not found" would make the
  // caller treat the first folder-with-children as a bogus ancestor.
  const findAncestorIds = useCallback(
    (folderId: string, folderList: TreeFolder[] = folders): string[] | null => {
      for (const folder of folderList) {
        if (folder.id === folderId) {
          return []; // Found it at this level, no ancestors needed
        }
        if (folder.children?.length) {
          const ancestorsBelow = findAncestorIds(folderId, folder.children);
          if (ancestorsBelow !== null) {
            // Found in children, add this folder as an ancestor
            return [folder.id, ...ancestorsBelow];
          }
        }
      }
      return null; // Not found in this branch
    },
    [folders]
  );

  // The controlled selection we last auto-expanded ancestors for. This effect
  // also re-runs whenever `folders` changes (findAncestorIds depends on it);
  // tracking the last-handled selection keeps the ancestor expansion from
  // re-firing on every tree rebuild and undoing a folder the user just
  // collapsed.
  const expandedForSelectionRef = useRef<string | null | undefined>(undefined);

  // Sync internal selection and expand ancestors when controlled selection changes
  useEffect(() => {
    if (controlledSelectedFolderId === undefined) return;

    // Sync internal state with controlled prop
    selectFolder(controlledSelectedFolderId);

    // Expand ancestors to reveal the selected folder, but only when the
    // controlled selection genuinely changes — re-running it on every render
    // would fight a manual collapse and immediately re-open the folder.
    if (
      controlledSelectedFolderId &&
      expandedForSelectionRef.current !== controlledSelectedFolderId
    ) {
      findAncestorIds(controlledSelectedFolderId)?.forEach((id) => expandFolder(id));
    }
    expandedForSelectionRef.current = controlledSelectedFolderId;
  }, [controlledSelectedFolderId, selectFolder, findAncestorIds, expandFolder]);

  // Wrap selectFolder to notify parent - toggle selection if same folder clicked
  const handleFolderSelect = useCallback(
    (folderId: string | null) => {
      if (folderId && selectedFolderId === folderId) {
        // Toggle off - clicking selected folder deselects it
        selectFolder(null);
        onSelectRoot?.();
      } else {
        selectFolder(folderId);
        if (folderId) {
          onFolderSelect?.(folderId);
        }
      }
    },
    [selectFolder, selectedFolderId, onFolderSelect, onSelectRoot]
  );
  const {
    editingFolderId,
    editName,
    startRename,
    confirmRename,
    cancelRename,
    setEditName,
    isEditing,
  } = useFolderRename((folderId, newName) => {
    onRenameFolder?.(folderId, newName);
  });

  const {
    dragOverId,
    isDraggedOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    handleDragOver,
  } = useFolderDragDrop(
    folders,
    {
      onDropDocument,
      onMoveFolder,
      onDropSelection,
      onDropFiles,
      // Hovering a collapsed parent reveals its children mid-drag, so a target
      // two levels down can be reached without aborting the drag to expand it
      // by hand. Only this view can do it: it is the only one that renders the
      // tree nested.
      onHoverExpand: startHoverExpand,
      onCancelHoverExpand: cancelHoverExpand,
    },
    true, // autoExpandEnabled
    activeMove?.folderIds ?? []
  );

  // Filter folders by search query
  const filteredFolders = useMemo(() => {
    if (!searchQuery.trim()) {
      return folders;
    }

    const query = searchQuery.toLowerCase();

    const matchesQuery = (folder: TreeFolder): boolean => {
      return folder.name.toLowerCase().includes(query);
    };

    const filterRecursive = (folder: TreeFolder): TreeFolder | null => {
      const matches = matchesQuery(folder);
      const filteredChildren = folder.children
        .map((child) => filterRecursive(child))
        .filter((child): child is TreeFolder => child !== null);

      // Include folder if it matches or has matching descendants
      if (matches || filteredChildren.length > 0) {
        return {
          ...folder,
          children: filteredChildren,
        };
      }

      return null;
    };

    return folders
      .map((folder) => filterRecursive(folder))
      .filter((folder): folder is TreeFolder => folder !== null);
  }, [folders, searchQuery]);

  // A tree node can also disappear mid-drag (the drop refetches the tree, and
  // the moved node may land inside a collapsed parent), so the node's own
  // `onDragEnd` is a best case, not a guarantee.
  const endMoveDrag = useCallback(() => onInternalDragChange?.(null), [onInternalDragChange]);
  useMoveDragTeardown(activeMove !== null, endMoveDrag);
  const pressedSelfHandledPart = useMoveDragOrigin();

  const handleFolderDragStart =
    (folder: TreeFolder) => (event: React.DragEvent<HTMLDivElement>) => {
      // The node itself is the drag source, so `event.target` is that node
      // whatever was pressed — the recorded pointer-down origin is what tells a
      // node drag from a press on its actions menu.
      if (pressedSelfHandledPart.current) {
        event.preventDefault();
        return;
      }
      const payload = writeMoveDragPayload(
        event.dataTransfer,
        folders,
        { kind: "folder", id: folder.id, label: folder.name },
        dragSelection,
        (count) => t.documents.folder.draggingItems.replace("{count}", String(count))
      );
      onInternalDragChange?.(payload);
    };

  // Recursive render function for folder and its children
  const renderFolder = (folder: TreeFolder, level: number): React.ReactNode => {
    const renderChildren = () => (
      <div className="animate-folder-expand space-y-0.5">
        {folder.children.map((child) => renderFolder(child, level + 1))}
      </div>
    );

    return (
      <FolderTreeNode
        key={folder.id}
        folder={folder}
        level={level}
        isExpanded={isExpanded(folder.id)}
        isSelected={selectedFolderId === folder.id}
        isEditing={isEditing(folder.id)}
        editName={editName}
        isDragOver={isDraggedOver(folder.id)}
        isDropAvailable={
          activeMove !== null && isEligibleMoveDropTarget(folders, folder.id, activeMove.folderIds)
        }
        isDraggable={
          !!onMoveFolder && !folder.isContainer && !isSystemFolder(folder) && !isBriefFolder(folder)
        }
        isDragging={activeMove?.folderIds.includes(folder.id) ?? false}
        onSelect={handleFolderSelect}
        onToggleExpand={toggleExpanded}
        onNameChange={setEditName}
        onNameConfirm={() => confirmRename(folder.id)}
        onNameCancel={cancelRename}
        onCreateSubfolder={(folderId) => onCreateSubfolder?.(folderId)}
        onRename={(folder) => startRename(folder)}
        onDelete={(folderId) => onDeleteFolder?.(folderId)}
        onExport={(folderId) => onExportFolder?.(folderId)}
        onOrganize={onOrganizeFolder}
        onShare={(folderId) => onShareFolder?.(folderId)}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragOver={handleDragOver(folder.id)}
        onDragStart={handleFolderDragStart(folder)}
        onDragEnd={endMoveDrag}
        renderChildren={folder.children.length > 0 ? renderChildren : undefined}
      />
    );
  };

  const addFolderButton = (
    <button
      type="button"
      disabled={!onCreateSubfolder}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
      onClick={() => onCreateSubfolder?.(null)}
    >
      <Plus className="h-4 w-4" />
      {t.projects.folder.addFolder}
    </button>
  );

  // The project root. Rendered ahead of the loading and empty states as well as
  // the tree, because it is not part of the tree: a project can hold files at
  // its root with no folders at all, and returning the empty state first left
  // that project with no root row, no root count and no way back to the root —
  // the files read as missing.
  const rootItem = !hideRootItem ? (
    <RootFolderItem
      name={rootName?.trim() || "Files"}
      documentCount={totalDocumentCount}
      isDragOver={isDraggedOver("root")}
      isDropAvailable={activeMove !== null}
      onSelect={() => {
        handleFolderSelect(null);
        onSelectRoot?.();
      }}
      onDragOver={handleDragOver(null)}
      onDragEnter={handleDragEnter("root", false)}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop(null)}
      onCreateFolder={() => onCreateSubfolder?.(null)}
      onExport={() => onExportFolder?.(null)}
    />
  ) : null;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-1">
        {rootItem}
        <div className="flex animate-pulse items-center justify-center py-12 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-primary" />
            Loading folders...
          </div>
        </div>
      </div>
    );
  }

  // Empty state (no folders at all)
  if (folders.length === 0) {
    return (
      <div className="space-y-2">
        {rootItem}
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-6 py-4">
            <p className="text-sm font-medium text-slate-600">{emptyMessage}</p>
          </div>
        </div>
        {addFolderButton}
      </div>
    );
  }

  // Empty search results — the root row stays for the same reason as above: a
  // search that matches no folder must not take away the way back to the root.
  if (searchQuery.trim() && filteredFolders.length === 0) {
    return (
      <div className="space-y-2">
        {rootItem}
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="rounded-lg border border-slate-100 bg-slate-50 px-6 py-4">
            <p className="text-sm font-medium text-slate-600">
              No folders found matching "{searchQuery}"
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      {/* Root folder item - hidden when project name shown separately */}
      {rootItem}

      {/* Folder tree */}
      <div className={hideRootItem ? "space-y-0.5" : "mt-1 space-y-0.5"}>
        {filteredFolders.map((folder) => renderFolder(folder, 0))}
      </div>

      {addFolderButton}
    </div>
  );
});
